/**
 * apps/automations-portal/src/router.tsx
 *
 * Route tree for Hattie Automations.
 *
 *   /                       redirects by role: admin → /admin,
 *                           client → /dashboard, none → access screen
 *   /sign-in                public
 *   /admin                  admin clients list
 *   /admin/clients/:id      admin client detail
 *   /admin/health           admin system health
 *   /demo                   PUBLIC — no auth required
 *   /dashboard              client overview
 *   *                       not found
 */
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { NotFoundPage } from '@/components/NotFoundPage';
import { useAuth } from '@/features/auth/AuthProvider';
import { AdminOnly, ClientOnly, NoAccessScreen, RequireAuth, useAccess } from '@/features/auth/guards';
import { SignInPage } from '@/features/auth/SignInPage';
import { ClientDetailPage } from '@/features/admin/ClientDetailPage';
import { ClientsPage } from '@/features/admin/ClientsPage';
import { HealthPage } from '@/features/admin/HealthPage';
import { LedgerApprovalsPage } from '@/features/admin/LedgerApprovalsPage';
import { LedgerClientThreadsPage } from '@/features/admin/LedgerClientThreadsPage';
import { LedgerLogPage } from '@/features/admin/LedgerLogPage';
import { LedgerOverviewPage } from '@/features/admin/LedgerOverviewPage';
import { DemoPage } from '@/features/demo/DemoPage';
import { OverviewPage } from '@/features/dashboard/OverviewPage';

const ADMIN_LINKS = [
  { to: '/admin', label: 'Clients' },
  { to: '/admin/health', label: 'System health' },
  { to: '/admin/ledger', label: 'Ledger' },
  { to: '/admin/ledger/log', label: 'Log' },
  { to: '/admin/ledger/approvals', label: 'Approvals' },
];

const CLIENT_LINKS = [{ to: '/dashboard', label: 'Overview' }];

function RoleRedirect() {
  const { user, loading } = useAuth();
  const { role } = useAccess();

  if (loading) return null;
  if (!user) return <Navigate to="/sign-in" replace />;
  if (role === 'loading') return null;
  if (role === 'admin') return <Navigate to="/admin" replace />;
  if (role === 'client') return <Navigate to="/dashboard" replace />;
  return <NoAccessScreen />;
}

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  { path: '/', element: <RoleRedirect /> },
  { path: '/sign-in', element: <SignInPage /> },
  { path: '/demo', element: <DemoPage /> },
  {
    path: '/admin',
    element: (
      <RequireAuth>
        <AdminOnly>
          <AppShell links={ADMIN_LINKS} />
        </AdminOnly>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <ClientsPage /> },
      { path: 'clients/:id', element: <ClientDetailPage /> },
      { path: 'health', element: <HealthPage /> },
      { path: 'ledger', element: <LedgerOverviewPage /> },
      { path: 'ledger/clients/:clientId', element: <LedgerClientThreadsPage /> },
      { path: 'ledger/log', element: <LedgerLogPage /> },
      { path: 'ledger/approvals', element: <LedgerApprovalsPage /> },
    ],
  },
  {
    path: '/dashboard',
    element: (
      <RequireAuth>
        <ClientOnly>
          <AppShell links={CLIENT_LINKS} />
        </ClientOnly>
      </RequireAuth>
    ),
    children: [{ index: true, element: <OverviewPage /> }],
  },
  { path: '*', element: <NotFoundPage /> },
]);
